/**
 * Where the gym lives in koomzoapps (kz2 M8): `/provider/app/gym/…` — the DS's `/gym/…`
 * (`data/gym.routes.ts`, `GYM_ROUTES`), one routed page per section.
 *
 * Root-light on purpose: `gym-attention.ts` (in the app's initial chunk) reads `GYM_RAIL`, so nothing
 * here may import the store, the records or a page.
 */

export const GYM_BASE = '/provider/app/gym';

/** The seven sections, each its own page. */
export type GymSection = 'desk' | 'members' | 'timetable' | 'bar' | 'assets' | 'trainers' | 'setup';

export const GYM_SECTIONS: readonly GymSection[] = ['desk', 'members', 'timetable', 'bar', 'assets', 'trainers', 'setup'];

/** The app's path for each DS section. */
export const GYM_PATH = {
  desk: `${GYM_BASE}/desk`,
  members: `${GYM_BASE}/members`,
  timetable: `${GYM_BASE}/timetable`,
  bar: `${GYM_BASE}/bar`,
  assets: `${GYM_BASE}/assets`,
  trainers: `${GYM_BASE}/trainers`,
  setup: `${GYM_BASE}/setup`,
  home: `${GYM_BASE}/home`,
} as const;

/**
 * The DS rail table (`GYM_ROUTES`, D110/D121): which capability owns which section. Three pairs
 * share a row — `classes|pt` → Timetable, `wallet|barpos` → Bar & wallet, `lockers|equipment` →
 * Lockers & kit — and nine capabilities are behaviours inside a screen, never a section (`null`).
 * The paths are the DS's `/gym/<section>`, which `gymCanSeeRoute` and the job guard read.
 */
export const GYM_ROUTES = {
  caps: {
    members: '/gym/members',
    classes: '/gym/timetable',
    pt: '/gym/timetable',
    wallet: '/gym/bar',
    barpos: '/gym/bar',
    lockers: '/gym/assets',
    equipment: '/gym/assets',
    commissions: '/gym/trainers',
    capacity: null,
    access: null,
    packages: null,
    freeze: null,
    corporate: null,
    tab: null,
    payout: null,
    whatsapp: null,
    churn: null,
  },
  /* The source's rail words (GM-19, D441). */
  labels: {
    members: 'Members',
    classes: 'Timetable',
    wallet: 'Bar & wallet',
    lockers: 'Lockers & kit',
  },
  setup: '/gym/setup',
  core: '/gym/desk',
} as const;

/** A DS route (`/gym/bar`) as the app's path (`/provider/app/gym/bar`). */
export function gymAppPath(dsRoute: string): string {
  return `${GYM_BASE}/${dsRoute.replace(/^\/gym\/?/, '')}`;
}

/**
 * The rail items the gym's badges decorate — the pages-config urls of its children (the DS's
 * badged rows, GM-19: Members, Timetable, Bar & wallet, Lockers & kit).
 */
export const GYM_RAIL = {
  members: GYM_PATH.members,
  timetable: GYM_PATH.timetable,
  bar: GYM_PATH.bar,
  assets: GYM_PATH.assets,
} as const;
